/**
 * Profile Page
 *
 * Displays user profile with sections for:
 * - Profile Information (editable)
 * - Course Enrollments
 * - Change Password
 *
 * Handles both current user profile (/profile) and admin viewing student (/students/:id)
 */

import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Login, UserRole } from '../apis/api-client';
import { usersClient } from '../apis/client-instances';
import {
  BarChartIcon,
  BookmarkIcon,
  CertificateIcon,
  EmailIcon,
  LogoutIcon,
  PersonIcon,
  VisibilityIcon,
} from '../components/icons';
import AssignmentsSection from '../components/profile/AssignmentsSection';
import CertificatesSection from '../components/profile/CertificatesSection';
import EnrollmentsSection from '../components/profile/EnrollmentsSection';
import ProfileInfoSection from '../components/profile/ProfileInfoSection';
import AssignContentDialog from '../components/users/AssignContentDialog';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '../components/ui';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { alert, getFileUrl } from '../hooks/function';
import { clearUser } from '../features/profile/profileSlice';
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding';
import { RootState } from '../types';
import { formatDate, getRoleLabel, getStatusColor, getUserDisplayName, getUserStatus, isAdmin } from '../utils/roleHelpers';
import { startImpersonation } from '../utils/impersonation';

type ProfileSection = 'info' | 'assignments' | 'enrollments' | 'certificates';

export default function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const navState = location.state as { from?: string; fromText?: string } | null;
  const { setLayoutConfig } = useLayoutConfig();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const { resetToAccountBranding } = useWorkspaceBranding();

  // Logout flow — same effect as the old sidebar popup, now lives here
  // since the sidebar avatar navigates straight to /profile.
  const handleLogout = () => {
    dispatch(clearUser());
    localStorage.setItem('token', '');
    localStorage.removeItem('viewAsWorkspaceId');
    // Strip workspace branding so the signin page falls back to the
    // canonical account branding (logo/colors/favicon).
    resetToAccountBranding();
    navigate('/signin');
  };

  const [user, setUser] = useState<Login | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState<ProfileSection>('info');
  // Resolved SAS-signed profile image URL — refetched whenever the
  // backing filename changes (e.g. after an upload).
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const userId = id ? parseInt(id) : typeof currentUser?.id === 'string' ? parseInt(currentUser.id) : currentUser?.id;
  const isOwnProfile = !id || userId === currentUser?.id;
  const userRole = currentUser?.role;

  // Admin viewing another *learner* can impersonate them — drops the
  // admin into the learner-facing dashboard for that user's workspace.
  // Other admin roles are excluded since impersonating another admin is
  // a riskier flow that warrants its own confirmation surface.
  const canImpersonate =
    !isOwnProfile &&
    isAdmin(userRole) &&
    user?.role === UserRole.User &&
    !!user?.id;

  // Admins can assign content to any non-self user (regardless of role
  // — assigning courses to other admins is a valid sales/onboarding
  // workflow). Sits in the same hero actions row as Impersonate.
  const canAssignContent = !isOwnProfile && isAdmin(userRole) && !!user?.id;
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => {
    // Skip when there's no userId to look up — typically because the user
    // just logged out and currentUser was cleared from Redux. Avoids a stray
    // "Failed to load profile" / "User not found" flash on the way to /signin.
    if (!userId) return;
    fetchUser();
  }, [userId]);

  // Configure back button when viewing a student profile (not own profile)
  // and hide the topbar's catalog search — nothing here searches courses.
  useEffect(() => {
    if (id) {
      // Viewing another user's profile - show back button.
      // If we arrived with explicit nav state (e.g., from /reports), honor it
      // so the back button returns to that exact tab/filter URL.
      const backUrl = navState?.from ?? '/students';
      const backText = navState?.fromText ?? 'Back to Users';
      setLayoutConfig({
        showBackButton: true,
        backButtonText: backText,
        backButtonAction: () => navigate(backUrl),
        hideGlobalSearch: true,
      });
    } else {
      // Viewing own profile ("My Account"). Learners have no sidebar, so
      // the topbar is their only nav — give them an explicit "Back to
      // dashboard" button (the clickable logo is the other way out).
      // Admins keep the sidebar, so they get no back button here.
      const isLearner = currentUser?.role === UserRole.User;
      setLayoutConfig({
        showBackButton: isLearner,
        backButtonText: isLearner ? 'Back to dashboard' : '',
        backButtonAction: isLearner ? () => navigate('/courses') : undefined,
        hideGlobalSearch: true,
      });
    }

    // Cleanup on unmount
    return () => {
      setLayoutConfig({
        showBackButton: false,
        backButtonText: '',
        backButtonAction: undefined,
        hideGlobalSearch: false,
      });
    };
  }, [id, navigate, setLayoutConfig, navState?.from, navState?.fromText, currentUser?.role]);

  // Resolve the profile-image URL to a displayable SAS-signed URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.profileImageUrl) {
        if (!cancelled) setAvatarUrl(null);
        return;
      }
      try {
        const url = await getFileUrl(user.profileImageUrl);
        if (!cancelled) setAvatarUrl(url || null);
      } catch {
        if (!cancelled) setAvatarUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.profileImageUrl]);

  // Impersonate this user — startImpersonation swaps to a real token
  // for them and reloads, so the app renders exactly as they'd see it.
  const [impersonating, setImpersonating] = useState(false);
  const handleImpersonate = async () => {
    if (!user?.id) return;
    setImpersonating(true);
    try {
      await startImpersonation(user.id, getUserDisplayName(user), `/students/${user.id}`);
    } catch (err: any) {
      alert(enqueueSnackbar, err?.message || 'Failed to impersonate', 'error');
      setImpersonating(false);
    }
  };

  const fetchUser = async (showLoading: boolean = true) => {
    try {
      if (showLoading) setLoading(true);

      if (!userId) {
        alert(enqueueSnackbar, 'User ID not found', 'error');
        return;
      }

      // Fetch user data - server handles authorization
      // Server will return 403 if user lacks permission to view this profile
      const result = await usersClient.getUserById(userId);
      setUser(result);
    } catch (error: any) {
      alert(enqueueSnackbar, 'Failed to load profile', 'error');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const sections: {
    id: ProfileSection;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { id: 'info', label: 'Profile Information', icon: <PersonIcon /> },
    { id: 'assignments', label: 'Assignments', icon: <BarChartIcon /> },
    { id: 'enrollments', label: 'Enrollments', icon: <BookmarkIcon /> },
    { id: 'certificates', label: 'Certificates', icon: <CertificateIcon /> },
  ];

  const renderSection = () => {
    if (!user) return null;

    const currentUserIdNum = typeof currentUser?.id === 'string' ? parseInt(currentUser.id) : currentUser?.id;

    switch (selectedSection) {
      case 'info':
        return <ProfileInfoSection user={user} currentUserId={currentUserIdNum} currentUserRole={userRole} onUpdate={() => fetchUser(false)} />;
      case 'assignments':
        return <AssignmentsSection userId={user.id!} />;
      case 'enrollments':
        return <EnrollmentsSection userId={user.id!} isAdmin={isAdmin(userRole)} />;
      case 'certificates':
        return <CertificatesSection userId={user.id!} isOwnProfile={isOwnProfile} />;
      default:
        return null;
    }
  };

  // If we're on /profile (own-profile mode) but the user is logged out,
  // ProtectedRoute is about to redirect to /signin — don't flash "User not
  // found" or a spinner in the meantime.
  if (!id && !currentUser) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h5" color="text.secondary">
          User not found
        </Typography>
      </Box>
    );
  }

  const displayName = getUserDisplayName(user);
  // `getUserStatus` returns the human-readable label directly ("Active",
  // "Inactive", "Pending Invitation", "Invitation Expired") and
  // `getStatusColor` picks the matching MUI palette — no case-sensitive
  // string comparisons needed.
  const statusLabel = getUserStatus(user);
  const statusColor = getStatusColor(user);
  const workspaceName = (user as any).workspaceName as string | undefined;
  const tags = user.tags || [];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ p: 4 }}>
          {/* ── Unified profile header ──────────────────────────────────
              One block at the top: avatar + name + role/status chips +
              identity meta + actions. Replaces the old split between a
              PageHeader and a hero card that both carried the name. */}
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 3,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Avatar
              src={avatarUrl ?? undefined}
              alt={displayName}
              sx={{
                width: 96,
                height: 96,
                bgcolor: avatarUrl ? 'transparent' : 'primary.main',
                color: 'white',
                fontSize: '2rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {displayName.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight={700}>
                  {displayName}
                </Typography>
                {user.role !== undefined && (
                  <Chip
                    label={getRoleLabel(user.role)}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      bgcolor: 'rgba(var(--brand-rgb), 0.18)',
                      color: 'primary.main',
                    }}
                  />
                )}
                <Chip
                  label={statusLabel}
                  size="small"
                  color={statusColor}
                  sx={{ height: 22, fontWeight: 600 }}
                />
              </Box>

              {/* Identity meta row — email, workspace, last login */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1, md: 2.5 },
                  color: 'text.secondary',
                }}
              >
                {user.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      {user.email}
                    </Typography>
                  </Box>
                )}
                {workspaceName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      <strong>Workspace:</strong> {workspaceName}
                    </Typography>
                  </Box>
                )}
                {user.lastLoginAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BarChartIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      Last login {formatDate(user.lastLoginAt as any)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Tag chips — admin-set metadata, kept subtle so the
                  identity row stays the focus. */}
              {tags.length > 0 && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {tags.map((t) => (
                    <Chip
                      key={t.id}
                      label={t.name}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontWeight: 500 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Actions — Impersonate + Assign content sit inside the
                hero so the whole identity block is one unit (avatar +
                meta + actions). Stacked vertically on narrow viewports
                so the buttons don't crush the meta row. */}
            {(canImpersonate || canAssignContent) && (
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', md: 'center' },
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                {canAssignContent && (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<BookmarkIcon />}
                    onClick={() => setAssignOpen(true)}
                    sx={{
                      px: 3,
                      borderColor: 'rgba(0,0,0,0.23)',
                      color: 'text.primary',
                      '&:hover': { borderColor: 'rgba(0,0,0,0.5)', bgcolor: 'action.hover' },
                    }}
                  >
                    Assign content
                  </Button>
                )}
                {canImpersonate && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={
                      impersonating ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <VisibilityIcon />
                      )
                    }
                    onClick={handleImpersonate}
                    disabled={impersonating}
                    sx={{
                      color: '#fff',
                      '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                      px: 3,
                    }}
                  >
                    {impersonating ? 'Switching…' : 'Impersonate'}
                  </Button>
                )}
              </Box>
            )}
          </Card>

          {/* ── Layout with sidebar navigation ───────────────────────── */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* Sidebar — pastel selected state + per-item icons. Same
                visual language as the curriculum builder rows. */}
            <Card
              sx={{
                width: 280,
                height: 'fit-content',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <List sx={{ p: 1 }}>
                {sections.map((section) => {
                  const isSelected = selectedSection === section.id;
                  return (
                    <ListItem key={section.id} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setSelectedSection(section.id)}
                        sx={{
                          borderRadius: 1.5,
                          py: 1.25,
                          px: 1.5,
                          gap: 0.5,
                          bgcolor: isSelected ? 'rgba(var(--brand-rgb), 0.16)' : 'transparent',
                          color: isSelected ? 'primary.main' : 'text.primary',
                          '&:hover': {
                            bgcolor: isSelected ? 'rgba(var(--brand-rgb), 0.22)' : 'rgba(var(--brand-rgb), 0.08)',
                          },
                          // Override the global Mui-selected style.
                          '&.Mui-selected, &.Mui-selected:hover': {
                            bgcolor: 'rgba(var(--brand-rgb), 0.16)',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: isSelected ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={section.label}
                          primaryTypographyProps={{
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: '0.95rem',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}

                {/* Logout — own profile only. Admins viewing a learner
                    shouldn't see a Logout option for that learner. */}
                {isOwnProfile && (
                  <ListItem
                    disablePadding
                    sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemButton
                      onClick={handleLogout}
                      sx={{
                        borderRadius: 1.5,
                        py: 1.25,
                        px: 1.5,
                        gap: 0.5,
                        color: 'error.main',
                        '&:hover': { bgcolor: 'error.softBg' },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Log out"
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Card>

            {/* Content Area */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Card
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                {renderSection()}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Assign-content dialog — opens from the hero action button. */}
      {user && (
        <AssignContentDialog
          open={assignOpen}
          targetUsers={[user]}
          onClose={() => setAssignOpen(false)}
        />
      )}
    </Box>
  );
}
